import { AiClientError, extractFiche } from '@/lib/ai/client';
import { navigate } from '@/lib/nav';
import { useFicheStore } from '@/lib/state/ficheStore';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useEffect, useRef, useState } from 'react';

type Stage = 'pending' | 'calling' | 'success' | 'error';

const STAGE_MESSAGES: Record<Stage, string> = {
  pending: 'Préparation…',
  calling: "L'IA lit tes sources et structure la fiche…",
  success: 'Fiche prête !',
  error: 'Échec de la génération',
};

export function GenerateRoute() {
  const sources = useSourceStore((s) => s.sources);
  const concatenated = useSourceStore((s) => s.concatenated);
  const putFiche = useFicheStore((s) => s.put);
  const [stage, setStage] = useState<Stage>('pending');
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [latency, setLatency] = useState<number | null>(null);
  const [modelUsed, setModelUsed] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (sources.length === 0) {
      navigate('/');
      return;
    }

    const blob = concatenated();
    if (!blob.trim()) {
      navigate('/');
      return;
    }

    setStage('calling');
    extractFiche({ blob, language: 'fr' })
      .then((out) => {
        putFiche(out.fiche);
        setLatency(out.latency_ms);
        setModelUsed(out.model_used);
        setStage('success');
        // Brief flash of the "ready" state before navigating
        setTimeout(() => navigate(`/editor/${out.fiche.id}`), 600);
      })
      .catch((err: unknown) => {
        if (err instanceof AiClientError) {
          setError(err.friendly);
          setErrorDetails(
            typeof err.details === 'string'
              ? err.details
              : err.details
                ? JSON.stringify(err.details, null, 2).slice(0, 400)
                : null,
          );
        } else {
          setError(err instanceof Error ? err.message : 'Erreur inconnue');
        }
        setStage('error');
      });
  }, [sources, concatenated, putFiche]);

  return (
    <main
      data-testid="generate-root"
      className="min-h-screen px-4 py-8"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(255,200,140,.18), transparent 40%), radial-gradient(circle at 80% 70%, rgba(140,180,255,.15), transparent 45%), linear-gradient(#ece5d2, #e0d8c0)',
      }}
    >
      <div
        className="relative max-w-xl mx-auto rounded-card px-6 md:px-10 py-12"
        style={{
          background: '#fdfaf1',
          backgroundImage:
            'linear-gradient(#e7dec8 1px, transparent 1px), linear-gradient(90deg, #e7dec8 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          backgroundPosition: '-1px -1px',
          border: '2.5px solid #1f2330',
          boxShadow:
            '0 1px 0 rgba(0,0,0,.04), 0 12px 28px rgba(60,40,20,.18), 0 30px 60px rgba(60,40,20,.12)',
        }}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <div
            className="bg-accent-yellow border-[2.5px] border-ink rounded-card px-5 py-1 shadow-ink-sm font-gloock text-xl"
            style={{ transform: 'rotate(-1deg)' }}
          >
            GÉNÉRATION
          </div>

          {stage !== 'error' ? (
            <>
              <div
                className={[
                  'w-16 h-16 rounded-full border-[3px] border-ink',
                  stage === 'success' ? 'bg-accent-green' : 'border-t-accent-blue animate-spin',
                ].join(' ')}
                aria-hidden
                style={
                  stage !== 'success'
                    ? { borderTopColor: '#2e6cb5', borderRightColor: '#1f2330' }
                    : {}
                }
              />
              <p className="font-hand text-[18px] text-ink">{STAGE_MESSAGES[stage]}</p>
              {stage === 'success' && modelUsed ? (
                <p className="font-caveat text-ink-soft text-[16px]">
                  {modelUsed} · {latency} ms
                </p>
              ) : null}
              <p className="font-caveat text-ink-soft text-[16px]">
                {sources.length} source{sources.length > 1 ? 's' : ''} · {concatenated().length}{' '}
                caractères
              </p>
            </>
          ) : (
            <>
              <div className="text-5xl" aria-hidden>
                😵
              </div>
              <h2 className="font-gloock text-[20px] text-warn-text">{STAGE_MESSAGES.error}</h2>
              <p
                className="font-hand text-[15px] text-ink max-w-[480px]"
                role="alert"
                data-testid="generate-error"
              >
                {error}
              </p>
              {errorDetails ? (
                <pre className="font-mono text-[11px] text-ink-soft bg-white/60 border-2 border-ink rounded-control p-2 max-w-full overflow-auto whitespace-pre-wrap">
                  {errorDetails}
                </pre>
              ) : null}
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    started.current = false;
                    setError(null);
                    setErrorDetails(null);
                    setStage('pending');
                    setTimeout(() => {
                      started.current = false;
                      // re-trigger by re-running the effect via a state bump
                      // Easiest: force a remount by going to / and back
                    });
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-white text-ink font-hand text-[15px] border-2 border-ink rounded-control shadow-ink-sm hover:translate-y-[-1px] transition-transform"
                >
                  ← Retour
                </button>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-accent-blue text-white font-hand text-[15px] border-2 border-ink rounded-control shadow-ink-sm hover:translate-y-[-1px] transition-transform"
                >
                  Réessayer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
